/**
 * Conversation Hooks
 *
 * Provides hooks for conversation CRUD operations using raClient.
 */

import {
  keepPreviousData,
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { raClient, type RAListParams } from '@/lib/raClient'
import { createQueryKeys } from '@/lib/queryKeys'
import type { Conversation, ConversationListFilters } from '@/types/conversation'

// ============================================================================
// Query Keys
// ============================================================================

export const conversationKeys = createQueryKeys('conversations')

// ============================================================================
// Types
// ============================================================================

interface UseConversationsOptions {
  filters?: ConversationListFilters
  page?: number
  perPage?: number
  sort?: { field: string; order: 'ASC' | 'DESC' }
  enabled?: boolean
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch paginated list of conversations
 */
export function useConversations({
  filters = {},
  page = 1,
  perPage = 25,
  sort = { field: 'updated_at', order: 'DESC' },
  enabled = true,
}: UseConversationsOptions = {}) {
  // Build filter object for react-admin format
  const filter: Record<string, unknown> = {}
  if (filters.assistant_id?.length) {
    filter.assistant_id = filters.assistant_id
  }
  if (filters.customer_id?.length) {
    // Use nested filter format for filtering by assistant's customer_id
    // The backend's apply_filters handles nested dicts by joining the related model
    filter.assistant = { customer_id: filters.customer_id }
  }
  if (filters.channel) {
    filter.channel = filters.channel
  }
  if (filters.outcome) {
    filter.outcome = filters.outcome
  }
  if (filters.tags?.length) {
    filter.tags = filters.tags
  }
  if (filters.date_from) {
    filter.date_from = filters.date_from
  }
  if (filters.date_to) {
    filter.date_to = filters.date_to
  }
  if (filters.topic) {
    filter.topic = `%${filters.topic}%`
  }
  if (filters.summary) {
    filter.summary = `%${filters.summary}%`
  }
  if (filters.from_id) {
    filter.from_id = filters.from_id
  }
  if (filters.backend_api_version) {
    // The backend wraps string filters in ILIKE '%...%', so a preset such as
    // '-rc.' matches every release candidate and an exact tag matches itself
    filter.backend_api_version = filters.backend_api_version
  }

  const params: RAListParams = { page, perPage, sort, filter }

  return useQuery({
    queryKey: conversationKeys.list(params),
    queryFn: () => raClient.getList<Conversation>('conversations', params),
    enabled,
  })
}

/**
 * Hook to fetch conversations page by page for an infinitely scrolling list.
 * Takes a ready-made react-admin filter.
 */
export function useInfiniteConversations(filter: Record<string, unknown>, perPage = 25) {
  return useInfiniteQuery({
    queryKey: [...conversationKeys.lists(), 'infinite', filter],
    queryFn: ({ pageParam }) =>
      raClient.getList<Conversation>('conversations', {
        page: pageParam,
        perPage,
        sort: { field: 'updated_at', order: 'DESC' },
        filter,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      pages.length * perPage < lastPage.total ? pages.length + 1 : undefined,
    // A new tag or date filter keeps the current rows until its own page arrives.
    placeholderData: keepPreviousData,
  })
}

/**
 * Hook to fetch a single conversation by ID
 */
export function useConversation(id: string | number | undefined) {
  return useQuery({
    queryKey: conversationKeys.detail(id!),
    queryFn: () => raClient.getOne<Conversation>('conversations', id!),
    enabled: !!id,
  })
}

/**
 * Hook to update a conversation
 */
export function useUpdateConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: Partial<Conversation> }) => {
      return raClient.update<Conversation>('conversations', id, data)
    },
    onSuccess: (data, variables) => {
      // Normalize ID to string (URL params are strings, API returns numbers)
      const id = String(variables.id)
      const numericId = Number(variables.id)

      // Update the specific conversation in cache
      queryClient.setQueryData(conversationKeys.detail(id), data)

      // Also update the conversation in any cached list queries
      queryClient.setQueriesData(
        { queryKey: conversationKeys.lists() },
        (oldData: { data: Conversation[]; total: number } | undefined) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            data: oldData.data.map((conv) => (conv.id === numericId ? { ...conv, ...data } : conv)),
          }
        }
      )

      // Invalidate detail query to ensure fresh data
      queryClient.invalidateQueries({ queryKey: conversationKeys.detail(id) })
      // Invalidate list queries to refresh
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() })
    },
  })
}

/**
 * Hook to delete a conversation
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string | number) => {
      await raClient.delete('conversations', id)
      return id
    },
    onSuccess: () => {
      // Invalidate all conversation queries
      queryClient.invalidateQueries({ queryKey: conversationKeys.all })
    },
  })
}

/**
 * Hook to get specific conversations by IDs
 */
export function useConversationsByIds(ids: number[], enabled = true) {
  return useQuery({
    queryKey: conversationKeys.byIds(ids),
    queryFn: () => raClient.getMany<Conversation>('conversations', ids),
    enabled: enabled && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  })
}
